import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";

// 🔑  Web3Forms access key — submissions arrive in the registered email.
//     Manage at https://web3forms.com (login with the email used at signup).
const WEB3FORMS_ACCESS_KEY = "99b37981-d14e-4e9c-b552-a25b86f04da7";

// hCaptcha — Web3Forms shared sitekey (no extra signup needed).
// To use your own, sign up at hcaptcha.com and replace this value.
const HCAPTCHA_SITEKEY = "50b2fe65-b00b-4b9e-ad62-3ba471098be2";

const initial = {
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
};

export default function Contact() {
    const [form, setForm] = useState(initial);
    const [status, setStatus] = useState({ state: "idle", error: "" });
    const [captchaToken, setCaptchaToken] = useState("");
    const navigate = useNavigate();
    const captchaContainerRef = useRef(null);
    const widgetIdRef = useRef(null);

    // Render hCaptcha once the script is available
    useEffect(() => {
        let cancelled = false;
        const tryRender = () => {
            if (cancelled) return;
            if (
                window.hcaptcha &&
                captchaContainerRef.current &&
                widgetIdRef.current === null
            ) {
                widgetIdRef.current = window.hcaptcha.render(
                    captchaContainerRef.current,
                    {
                        sitekey: HCAPTCHA_SITEKEY,
                        theme: "light",
                        size: "normal",
                        callback: (token) => setCaptchaToken(token),
                        "expired-callback": () => setCaptchaToken(""),
                        "error-callback": () => setCaptchaToken(""),
                    }
                );
            } else if (!window.hcaptcha) {
                setTimeout(tryRender, 250);
            }
        };
        tryRender();
        return () => {
            cancelled = true;
        };
    }, []);

    const resetCaptcha = () => {
        try {
            if (window.hcaptcha && widgetIdRef.current !== null) {
                window.hcaptcha.reset(widgetIdRef.current);
            }
        } catch (_) {
            /* ignore reset errors */
        }
        setCaptchaToken("");
    };

    const update = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email) {
            setStatus({
                state: "error",
                error: "Name and email are required.",
            });
            return;
        }
        if (!captchaToken) {
            setStatus({
                state: "error",
                error: "Please complete the captcha before sending.",
            });
            return;
        }
        setStatus({ state: "loading", error: "" });
        try {
            const res = await fetch("https://api.web3forms.com/submit", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    access_key: WEB3FORMS_ACCESS_KEY,
                    subject: `New inquiry from ${form.name} — Vyom website`,
                    from_name: "Vyom website",
                    name: form.name,
                    company: form.company,
                    email: form.email,
                    phone: form.phone,
                    message: form.message,
                    "h-captcha-response": captchaToken,
                }),
            });
            const data = await res.json();
            if (data && data.success) {
                setForm(initial);
                resetCaptcha();
                navigate("/thank-you");
            } else {
                resetCaptcha();
                setStatus({
                    state: "error",
                    error:
                        (data && data.message) ||
                        "Something went wrong. Please try again.",
                });
            }
        } catch (err) {
            resetCaptcha();
            setStatus({
                state: "error",
                error: "Network error. Please try again in a moment.",
            });
        }
    };

    return (
        <div data-testid="page-contact" className="pt-32">
            <Seo
                title="Contact — Begin a Conversation"
                description="Contact Vyom Tex Linen LLP — bed linen, blanket, bedsheet, duvet, pillow cover, quilt and bath towel manufacturer in Coimbatore, Tamil Nadu, India. We respond within two working days."
                path="/contact"
            />
            <section className="container-vyom pb-16 md:pb-20">
                <Reveal>
                    <div className="text-[17.5px] uppercase tracking-overline text-vyom-mute mb-8">
                        Contact
                    </div>
                </Reveal>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-end">
                    <div className="md:col-span-7">
                        <Reveal delay={0.05}>
                            <h1 className="font-serif-display text-5xl md:text-7xl lg:text-[96px] leading-[0.98] tracking-tight">
                                Begin a{" "}
                                <span className="italic">
                                    conversation.
                                </span>
                            </h1>
                        </Reveal>
                    </div>
                    <div className="md:col-span-4 md:col-start-9">
                        <Reveal delay={0.15}>
                            <p className="text-[19px] leading-relaxed text-vyom-mute max-w-md">
                                Tell us about the house you are building. We
                                respond within two working days.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="border-t border-vyom-rule">
                <div className="container-vyom section-pad grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
                    <aside className="md:col-span-4 space-y-10">
                        <div>
                            <div className="text-[17.5px] uppercase tracking-overline text-vyom-mute mb-3">
                                Studio
                            </div>
                            <div className="text-[19.5px] leading-relaxed text-vyom-ink/90">
                                Vyom Tex Linen LLP
                                <br />
                                1/482F, Transport Nagar,
                                <br />
                                Near Maharaja Theme Park,
                                <br />
                                Neelambur, Coimbatore,
                                <br />
                                Tamil Nadu — 641014, India
                            </div>
                        </div>
                        <div>
                            <div className="text-[17.5px] uppercase tracking-overline text-vyom-mute mb-3">
                                Direct
                            </div>
                            <a
                                href="tel:+918220055388"
                                className="text-[19.5px] text-vyom-ink link-underline"
                                data-testid="contact-phone"
                            >
                                +91 82200 55388
                            </a>
                        </div>
                        <div>
                            <div className="text-[17.5px] uppercase tracking-overline text-vyom-mute mb-3">
                                Hours
                            </div>
                            <div className="text-[19.5px] text-vyom-ink/90">
                                Mon — Sat
                                <br />
                                09:30 — 18:00 IST
                            </div>
                        </div>
                    </aside>

                    <form
                        data-testid="contact-form"
                        onSubmit={submit}
                        className="md:col-span-7 md:col-start-6 space-y-10"
                        noValidate
                    >
                        <Field
                            label="Name"
                            id="name"
                            value={form.name}
                            onChange={update("name")}
                            required
                            testid="field-name"
                        />
                        <Field
                            label="Company"
                            id="company"
                            value={form.company}
                            onChange={update("company")}
                            testid="field-company"
                        />
                        <Field
                            label="Email"
                            id="email"
                            type="email"
                            value={form.email}
                            onChange={update("email")}
                            required
                            testid="field-email"
                        />
                        <Field
                            label="Phone"
                            id="phone"
                            value={form.phone}
                            onChange={update("phone")}
                            testid="field-phone"
                        />
                        <FieldArea
                            label="A few words about your project"
                            id="message"
                            value={form.message}
                            onChange={update("message")}
                            testid="field-message"
                        />

                        {/* hCaptcha widget */}
                        <div className="pt-2">
                            <div
                                ref={captchaContainerRef}
                                data-testid="contact-captcha"
                                aria-label="Spam protection — please complete the captcha"
                            />
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center gap-6 pt-4">
                            <button
                                type="submit"
                                disabled={status.state === "loading"}
                                data-testid="contact-submit"
                                className="text-[19px] whitespace-nowrap uppercase tracking-overline border border-vyom-ink px-8 py-3.5 text-vyom-ink hover:bg-vyom-ink hover:text-vyom-bg transition-colors duration-500 disabled:opacity-50"
                            >
                                {status.state === "loading"
                                    ? "Sending…"
                                    : "Send inquiry"}
                            </button>

                            {status.state === "error" && (
                                <span
                                    data-testid="contact-error"
                                    className="text-[15px] text-vyom-ink"
                                >
                                    {status.error}
                                </span>
                            )}
                        </div>

                        <p className="text-[12px] leading-relaxed text-vyom-mute pt-2 max-w-md">
                            By sending an inquiry you agree to be contacted by
                            our team regarding your request. We do not share
                            your details with third parties.
                        </p>
                    </form>
                </div>
            </section>
        </div>
    );
}

function Field({ label, id, type = "text", value, onChange, required, testid }) {
    return (
        <div className="border-b border-vyom-rule pb-3 focus-within:border-vyom-ink transition-colors duration-500">
            <label
                htmlFor={id}
                className="block text-[20px] uppercase tracking-overline text-vyom-mute mb-2"
            >
                {label}
                {required ? " *" : ""}
            </label>
            <input
                id={id}
                name={id}
                type={type}
                value={value}
                onChange={onChange}
                required={required}
                data-testid={testid}
                className="w-full bg-transparent outline-none text-[20px] text-vyom-ink placeholder-vyom-mute py-1"
                autoComplete="off"
            />
        </div>
    );
}

function FieldArea({ label, id, value, onChange, testid }) {
    return (
        <div className="border-b border-vyom-rule pb-3 focus-within:border-vyom-ink transition-colors duration-500">
            <label
                htmlFor={id}
                className="block text-[20px] uppercase tracking-overline text-vyom-mute mb-2"
            >
                {label}
            </label>
            <textarea
                id={id}
                name={id}
                value={value}
                onChange={onChange}
                rows={4}
                data-testid={testid}
                className="w-full bg-transparent outline-none text-[20px] text-vyom-ink placeholder-vyom-mute py-1 resize-none"
            />
        </div>
    );
}
